// src/supabase.js
// Cliente Supabase para uso no frontend
// As chaves vêm das variáveis de ambiente (nunca hardcoded)

import { createClient } from "@supabase/supabase-js";

const supabaseUrl  = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnon = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnon) {
  console.warn("⚠️ Supabase não configurado. Adicione as variáveis de ambiente.");
}

export const supabase = createClient(
  supabaseUrl  || "https://placeholder.supabase.co",
  supabaseAnon || "placeholder"
);

// ── HELPERS ───────────────────────────────────────────────────

/** Busca ou cria a institution do usuário logado (Supabase Auth) */
export async function getOrCreateInstitution(user) {
  const userId = user?.id;
  const email  = user?.email;

  if (!userId) {
    console.error("[Supabase] user sem id:", user);
    return { institution: null, error: new Error("Usuário sem ID") };
  }

  // Tenta buscar
  // Nota: a coluna se chama cognito_user_id por razões históricas;
  // agora armazena o UUID do Supabase Auth. Renomeie para user_id via migration se quiser.
  const { data, error } = await supabase
    .from("institutions")
    .select("*")
    .eq("cognito_user_id", userId)
    .single();

  if (data) return { institution: data, error: null };

  if (error && error.code !== "PGRST116") {
    // PGRST116 = not found (esperado na criação), outros são erros reais
    console.error("[Supabase] Erro ao buscar institution:", error);
    return { institution: null, error };
  }

  // Cria se não existir
  const { data: created, error: createError } = await supabase
    .from("institutions")
    .insert({ cognito_user_id: userId, name: email || userId, email: email || null })
    .select()
    .single();

  if (createError) console.error("[Supabase] Erro ao criar institution:", createError);
  return { institution: created, error: createError };
}

/** Lista alunos da institution */
export async function listStudents(institutionId) {
  return supabase
    .from("students")
    .select("*")
    .eq("institution_id", institutionId)
    .eq("active", true)
    .order("name");
}

/** Cria aluno */
export async function createStudent(data) {
  return supabase.from("students").insert(data).select().single();
}

/** Atualiza aluno */
export async function updateStudent(id, data) {
  return supabase.from("students").update(data).eq("id", id).select().single();
}

/** Remove aluno (soft delete) */
export async function deactivateStudent(id) {
  return supabase.from("students").update({ active: false }).eq("id", id);
}

/**
 * Busca token pelo UUID (usado no StudentForm — sem auth).
 * Usa a RPC get_form_token (SECURITY DEFINER): anon não lê form_tokens direto.
 * Retorna { data: { id, escala_id, status, expires_at } | null, error }.
 */
export async function getFormToken(tokenId) {
  const { data, error } = await supabase.rpc("get_form_token", { token_uuid: tokenId });
  return { data: data || null, error };
}

/**
 * Salva resposta do aluno via RPC submit_form_response (atômica).
 * Aluno, instituição e escala são derivados do token no banco — o cliente
 * não envia ids. Retorna { data: <uuid da resposta>, error }.
 */
export async function saveResponse({ tokenId, respostas, pontuacao, nivelRisco, labelResultado }) {
  const { data, error } = await supabase.rpc("submit_form_response", {
    token_uuid:        tokenId,
    p_respostas:       respostas,
    p_pontuacao:       pontuacao,
    p_nivel_risco:     nivelRisco,
    p_label_resultado: labelResultado,
  });
  return { data, error };
}

/** Dispara envio em massa via Edge Function */
export async function sendFormsBatch({ escalaId, studentIds, institutionId }) {
  return supabase.functions.invoke("send-forms", {
    body: { escala_id: escalaId, student_ids: studentIds, institution_id: institutionId },
  });
}

/** Busca respostas da institution para o dashboard */
export async function listResponses(institutionId) {
  return supabase
    .from("responses")
    .select("*, students(name, year, class)")
    .eq("institution_id", institutionId)
    .order("answered_at", { ascending: false });
}

/**
 * Atualiza status de consentimento de um aluno.
 * field: "consent_signed" | "assent_signed" | "terms_signed"
 */
export async function updateStudentConsent(studentId, field, value) {
  const atField = field.replace("_signed", "_signed_at");
  return supabase
    .from("students")
    .update({ [field]: value, [atField]: value ? new Date().toISOString() : null })
    .eq("id", studentId)
    .select()
    .single();
}

/** Dispara envio em massa de termos via Edge Function */
export async function sendTermsBatch({ studentIds, institutionId, termTypes }) {
  return supabase.functions.invoke("send-terms", {
    body: { student_ids: studentIds, institution_id: institutionId, term_types: termTypes },
  });
}
